import type {
  GoogleAdsAccountsResponse,
  GoogleAdsSelectableSubAccount,
} from '@common/api/authService';

export type GoogleAdsRow = GoogleAdsSelectableSubAccount & {
  loginCustomerName?: string | null;
};

export interface GoogleAdsAccountRequestScope {
  workspaceId: string;
  connectionId: string | null;
  generation: number;
}

export const createGoogleAdsAccountRequestScope = (
  workspaceId: string,
  connectionId: string | null | undefined,
  generation: number,
): GoogleAdsAccountRequestScope => ({
  workspaceId,
  connectionId: connectionId || null,
  generation,
});

export const isSameGoogleAdsAccountRequestScope = (
  left: GoogleAdsAccountRequestScope | null,
  right: GoogleAdsAccountRequestScope | null,
) => Boolean(
  left
  && right
  && left.workspaceId === right.workspaceId
  && left.connectionId === right.connectionId
  && left.generation === right.generation
);

export const normalizeCustomerId = (value?: string | null) =>
  String(value || '').trim();

export const getAccountName = (account: GoogleAdsSelectableSubAccount) =>
  account.name || account.id || 'Google Ads customer';

export const getDisabledLabel = (account: GoogleAdsSelectableSubAccount) => {
  if (
    account.disabled_reason === 'manager_account' ||
    account.manager_account ||
    account.is_manager
  ) {
    return 'Manager account';
  }
  if (account.status && String(account.status).toUpperCase() !== 'ENABLED') {
    return String(account.status);
  }
  return 'Unavailable';
};

export const buildGoogleAdsRows = (
  accounts: GoogleAdsAccountsResponse['accounts'] = [],
) => {
  const seenCustomerIds = new Set<string>();
  return accounts.flatMap((group) =>
    (group.sub_accounts || []).flatMap((account) => {
      const customerId = normalizeCustomerId(account.id);
      if (!customerId || seenCustomerIds.has(customerId)) {
        return [];
      }
      seenCustomerIds.add(customerId);
      return [
        {
          ...account,
          id: customerId,
          login_customer_id: account.login_customer_id || group.login_customer_id,
          loginCustomerName: group.login_customer_name,
        },
      ];
    }),
  );
};
